import axios from 'axios';

const DIRECTUS_INSTANCE = 'https://api.prosecaptain.com';
const WIDGET_API_BASE = process.env.REACT_APP_WIDGET_API_URL || 'https://widget-api.prosecaptain.com';

const directusClient = axios.create({
  baseURL: DIRECTUS_INSTANCE,
  headers: { 'Content-Type': 'application/json' }
});

directusClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('directus_token') || sessionStorage.getItem('directus_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const parseErrorMessage = (data, fallback) => data?.error || data?.message || fallback;

export const getWorkspaceToken = async () => {
  const authToken = localStorage.getItem('directus_token') || sessionStorage.getItem('directus_token');
  if (!authToken) {
    throw new Error('Missing Directus auth token');
  }

  const userResponse = await directusClient.get('/users/me', {
    params: { fields: ['linked_workspace.id', 'linked_workspace'] }
  });

  const linkedWorkspace = userResponse.data?.data?.linked_workspace;
  const workspaceId = typeof linkedWorkspace === 'object' ? linkedWorkspace?.id : linkedWorkspace;
  if (!workspaceId) {
    throw new Error('No linked workspace found for current user');
  }

  const workspaceResponse = await directusClient.get(`/items/workspace/${workspaceId}`, {
    params: { fields: ['widget_token'] }
  });

  const widgetToken = workspaceResponse.data?.data?.widget_token;
  if (!widgetToken) {
    throw new Error('Workspace does not have widget_token configured');
  }

  return widgetToken;
};

export const createWidgetThread = async (widgetToken, context = null, widgetId = null) => {
  if (!widgetToken) {
    throw new Error('Widget token is required');
  }

  const query = `token=${encodeURIComponent(widgetToken)}`;
  const payload = {
    ...(context ? { context } : {}),
    ...(widgetId ? { widget_id: widgetId } : {})
  };

  const response = await fetch(`${WIDGET_API_BASE}/api/v2/widget/thread?${query}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(parseErrorMessage(data, `Widget thread init failed (${response.status})`));
  }

  return data;
};

export const sendWidgetMessage = async (widgetToken, sessionId, message, widgetId = null, pageContext = null) => {
  if (!widgetToken || !sessionId || !message) {
    throw new Error('Widget token, sessionId and message are required');
  }

  const query = `token=${encodeURIComponent(widgetToken)}`;
  const payload = {
    session_id: sessionId,
    message,
    ...(widgetId ? { widget_id: widgetId } : {}),
    ...(pageContext ? { page_context: pageContext } : {})
  };

  const response = await fetch(`${WIDGET_API_BASE}/api/v2/widget/message?${query}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(parseErrorMessage(data, `Widget message failed (${response.status})`));
  }

  return data;
};
